/* eslint-disable jsx-a11y/anchor-is-valid */
import _ from 'underscore';
import React, { useContext, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Link } from '@fluentui/react';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getStyles } from './styles';
import { Tile } from '../tile';
import { ACCOUNT_MODE, ACCOUNT_MODE_INFO } from './consts';
import { ModeItem } from './mode-item';
import { IntroTexts } from './intro-texts';

export function AccountModeSelection(props) {
  const {
    initSelectedMode,
    selectedMode,
    onModeSelectionChange,
    useConfirmDialog,
    uiActivity,
    shouldShowAccountModeChangedWarning,
    shouldShowAccountModeChangedWarningForSkip,
    subtitle,
    onNext,
    onBack,
    onSkip,
    displayMode,
  } = props;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const [isSkip, setIsSkip] = useState(false);

  const subtitleText = i18n.getString(_TL_('Choose the account mode that will best help you achieve your goals. {{a}}Learn more about Smart mode vs. Expert mode{{/a}}'));
  const subtitleNode = useMemo(() => subtitle || (
    <p className={classes.accountModeSubtitle}>
      <ReactTemplate
        model={{
          a: text => (
            <Link
              className="account-mode-comparison-link"
              href="https://help.ads.microsoft.com/#apex/ads/en/56915/-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              {text}
            </Link>),
        }}
        template={subtitleText}
      />
    </p>
  ), [classes.accountModeSubtitle, subtitle, subtitleText]);

  const onConfirmNext = useCallback(() => {
    if (initSelectedMode !== selectedMode) {
      uiActivity.trace(`User update accountMode from ${initSelectedMode} to ${selectedMode}`);
    }

    if (isSkip) {
      onSkip();
    } else {
      onNext(selectedMode);
    }
  }, [initSelectedMode, selectedMode, onNext, uiActivity, isSkip, onSkip]);

  const onCancelDialog = useCallback(() => {
    setIsSkip(false);
  }, []);

  const [
    changeDialog,
    showChangeDialog,
    isChangeDialogVisible,
  ] = useConfirmDialog({
    title: i18n.getString(_TL_('Confirm changes')),
    content: initSelectedMode === ACCOUNT_MODE.UNIFIED_SMART ?
      i18n.getString(_TL_('When you confirm a change to the account mode, the associated campaign and social profile will be deleted.')) :
      i18n.getString(_TL_('When you confirm a change to the account mode, the associated campaign will be deleted.')),
    confirmText: i18n.getString(_TL_('Confirm')),
    onConfirm: onConfirmNext,
    cancelText: i18n.getString(_TL_('Discard changes')),
    onCancel: onCancelDialog,
    className: 'wizard-change-account-mode-dialog',
  }, {
    activity: uiActivity,
    dialogName: 'WizardChangeAccountModeDialog',
  });

  const onSkipClicked = useCallback(() => {
    if (shouldShowAccountModeChangedWarningForSkip) {
      setIsSkip(true);
      showChangeDialog();
    } else {
      onSkip();
    }
  }, [shouldShowAccountModeChangedWarningForSkip, showChangeDialog, onSkip]);

  const skipText = i18n.getString(_TL_('Do you want to create another campaign? {{a}}Create an account first.{{/a}}'));
  const skipNode = useMemo(() => (
    <ReactTemplate
      model={{
        a: text => (
          <Link
            className="account-mode-skip-link"
            onClick={onSkipClicked}
          >
            {text}
          </Link>),
      }}
      template={skipText}
    />
  ), [skipText, onSkipClicked]);

  const onBackClicked = useCallback(() => {
    onBack(selectedMode);
  }, [onBack, selectedMode]);

  const onNextClicked = useCallback(() => {
    if (shouldShowAccountModeChangedWarning && selectedMode !== initSelectedMode) {
      showChangeDialog();
    } else {
      onNext(selectedMode);
    }
  }, [selectedMode, initSelectedMode, shouldShowAccountModeChangedWarning, showChangeDialog, onNext]);

  const onSmartModeClicked = useCallback(() => {
    onModeSelectionChange(ACCOUNT_MODE.UNIFIED_SMART);
  }, [onModeSelectionChange]);

  const onExpertModeClicked = useCallback(() => {
    onModeSelectionChange(ACCOUNT_MODE.EXPERT);
  }, [onModeSelectionChange]);

  return (
    <Tile
      title={i18n.getString(_TL_('Which experience do you prefer?'))}
      subtitle={subtitleNode}
      onNext={onNextClicked}
      onBack={onBackClicked}
      nextBtnText={i18n.getString(_TL_('Create your campaign'))}
      displayMode={displayMode}
    >
      <div className={classNames('account-mode-container', classes.accountModeContainer, classes[`accountModeContainer-${displayMode}`])}>
        <div className={classNames('account-mode-selection-container', classes.accountModeSelectionContainer, classes[`accountModeSelectionContainer-${displayMode}`])}>
          <ModeItem
            selected={selectedMode === ACCOUNT_MODE.UNIFIED_SMART}
            item={ACCOUNT_MODE_INFO[ACCOUNT_MODE.UNIFIED_SMART]}
            displayMode={displayMode}
            onClick={onSmartModeClicked}
            isRecommended
          />
          <ModeItem
            selected={selectedMode === ACCOUNT_MODE.EXPERT}
            item={ACCOUNT_MODE_INFO[ACCOUNT_MODE.EXPERT]}
            displayMode={displayMode}
            onClick={onExpertModeClicked}
          />
        </div>
        <IntroTexts
          introTitle={ACCOUNT_MODE_INFO[selectedMode].introTitle}
          introTexts={ACCOUNT_MODE_INFO[selectedMode].introTexts}
          displayMode={displayMode}
        />
        {selectedMode === ACCOUNT_MODE.EXPERT &&
          <div className={classNames('account-mode-skip-container', classes.accountModeSkipContainer, classes[`accountModeSkipContainer-${displayMode}`])}>
            {skipNode}
          </div>}
      </div>
      {isChangeDialogVisible && changeDialog}
    </Tile>
  );
}

AccountModeSelection.propTypes = {
  initSelectedMode: PropTypes.oneOf(_.values(ACCOUNT_MODE)),
  selectedMode: PropTypes.oneOf(_.values(ACCOUNT_MODE)),
  onModeSelectionChange: PropTypes.func.isRequired,
  useConfirmDialog: PropTypes.func.isRequired,
  uiActivity: PropTypes.shape({
    trace: PropTypes.func,
  }).isRequired,
  shouldShowAccountModeChangedWarning: PropTypes.bool.isRequired,
  shouldShowAccountModeChangedWarningForSkip: PropTypes.bool.isRequired,
  subtitle: PropTypes.node,
  onBack: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  displayMode: PropTypes.string,
};

AccountModeSelection.defaultProps = {
  initSelectedMode: ACCOUNT_MODE.UNIFIED_SMART,
  selectedMode: ACCOUNT_MODE.UNIFIED_SMART,
  subtitle: null,
  displayMode: '',
};
